/**
 * @module App
 * @description Root component for the XAI Study application.
 *
 * Flow:
 * 1. On mount, read Prolific URL params (PROLIFIC_PID, STUDY_ID, SESSION_ID).
 * 2. If Prolific params present → request mode assignment from backend.
 * 3. Show StartingQuestionnaire (step 1 auto-filled when Prolific).
 * 4. Navigate through phases × images, with after-image and after-phase forms.
 * 5. Show EndingQuestionnaire, then Prolific redirect (or thank-you page).
 *
 * Eye-tracking and mouse tracking have been removed.
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import CaptionText from "./components/CaptionText";
import ImageWithBoxes from "./components/ImageWithBoxes";
import DebugView from "./components/DebugView";
import AdminView from "./components/AdminView";
import { PHASE_ORDER, PHASE_DISPLAY_NAMES, CONDITION_MAP, DEBUG_MODE, CURRENT_MODE } from "./config/appConfig";
import "./App.css";
import TextualExplanation from "./components/TextualExplanation";
import VisualTextualExplanation from "./components/VisualTextualExplanation";
import StartingQuestionnaire from "./components/forms/StartingQuestionnaire";
import AfterImageQuestionnaire from "./components/forms/AfterImageQuestionnaire";
import AfterPhaseQuestionnaire from "./components/forms/AfterPhaseQuestionnaire";
import EndingQuestionnaire from "./components/forms/EndingQuestionnaire";
import ConsentForm from "./components/forms/ConsentForm";
import Tutorial from "./components/Tutorial";
import { Button } from "@mui/material";
import { AUSTRIA_INTRO_PEOPLE, NORWAY_INTRO_PEOPLE } from "./config/introPeople";
import { getOptimizedImageUrl } from "./utils/imagePaths";

// Hooks
import { useLogging } from "./hooks/useLogging";
import { useQuestionnaireManager } from "./hooks/useQuestionnaireManager";
import { useModeImageManager } from "./hooks/useModeImageManager";
import { useProlificParams } from "./hooks/useProlificParams";

// API
import { getAssignedMode, markComplete } from "./api/prolific";
import { isDebugPrefillParticipantCode } from "./utils/debugPrefill";
import { loadSession, saveSession, clearSession } from "./utils/sessionPersistence";

// Per-phase intro screens shown before a trip begins. Icons avoid flag emojis
// (which don't render on some platforms).
const PHASE_INTROS = {
  Erroneous: {
    title: "🏔️ Hiking Trip in Austria",
    people: AUSTRIA_INTRO_PEOPLE,
    paragraphs: [
      "The next trip follows Marie, James, and Daniel through alpine trails in Austria. You will continue rating each AI-generated caption for the photobook.",
      "Look carefully at the people, places, actions, and mountain details before choosing your caption quality rating.",
    ],
  },
  Recovery: {
    title: "🏞️ Norway Trip",
    people: NORWAY_INTRO_PEOPLE,
    paragraphs: [
      "The final trip follows Marie and James through the fjords and scenery of Norway. You will continue rating each AI-generated caption for the photobook.",
      "Look carefully at the people, places, actions, and scenery before choosing your caption quality rating.",
    ],
  },
};

function AppRouter() {
  const isAdmin = new URLSearchParams(window.location.search).get("admin") === "1";
  const isDebug = new URLSearchParams(window.location.search).get("debug") === "1";
  if (isAdmin) return <AdminView />;
  if (DEBUG_MODE && isDebug) return <DebugView />;
  return <App />;
}

function App() {
  // ---- Prolific / test-mode integration ----
  const { prolificPid, studyId, testUser, testCond, isTestMode } = useProlificParams();
  const debugControlsEnabled = new URLSearchParams(window.location.search).get("debug") === "1";

  // ---- Refresh-resume: restore any saved progress for this participant ----
  // Keyed by the Prolific PID (or test user) so a refresh continues where the
  // participant left off instead of restarting and duplicating submissions.
  const sessionKey = prolificPid || testUser || "local";
  const restored = useRef(loadSession(sessionKey)).current;
  const persist = useCallback(
    (patch) => saveSession(sessionKey, patch),
    [sessionKey]
  );
  // Resolve test-mode condition synchronously so StartingQuestionnaire's
  // useState initializer captures the correct assignedMode on first render.
  const [prolificMode, setProlificMode] = useState(() => {
    if (isTestMode) {
      const resolved = testCond ? CONDITION_MAP[testCond] || testCond : CURRENT_MODE;
      console.log(`[App] TestMode init: cond=${testCond}, resolved="${resolved}", CONDITION_MAP=`, CONDITION_MAP);
      return resolved;
    }
    // Reuse the already-assigned condition on refresh (the backend is
    // idempotent anyway, but this avoids a needless round-trip and flicker).
    return restored?.prolificMode || null;
  });
  const [prolificRedirectUrl, setProlificRedirectUrl] = useState(null);
  const [prolificLoading, setProlificLoading] = useState(false);
  const [prolificError, setProlificError] = useState(null);

  // ---- Refs to break circular deps between hooks ----
  const setImageIndexRef = useRef(() => {});
  const setPhaseIndexRef = useRef(() => {});
  const setShowAfterImageQuestionnaireRef = useRef(() => {});
  const participantCodeRef = useRef(null);

  // ---- Assigned mode as reactive state (not a ref) ----
  const [assignedMode, setAssignedMode] = useState(restored?.assignedMode || null);
  const [tutorialCompleted, setTutorialCompleted] = useState(!!restored?.tutorialCompleted);
  const [accessData, setAccessData] = useState(restored?.accessData || null);
  const [consentData, setConsentData] = useState(restored?.consentData || null);
  const [consentDeclined, setConsentDeclined] = useState(false);
  const [acknowledgedPhaseIntros, setAcknowledgedPhaseIntros] = useState(
    restored?.acknowledgedPhaseIntros || {}
  );

  // Persist the resumable milestones whenever they change.
  useEffect(() => {
    persist({
      accessData,
      consentData,
      prolificMode,
      assignedMode,
      tutorialCompleted,
      acknowledgedPhaseIntros,
    });
  }, [persist, accessData, consentData, prolificMode, assignedMode, tutorialCompleted, acknowledgedPhaseIntros]);

  // Fetch the mode assignment ONLY after the participant has consented. The
  // backend round-robin counter advances on this call, so deferring it until
  // consent means people who bounce on the landing/consent screen or decline
  // consent never consume a counter-balancing slot (keeping conditions balanced
  // among people who actually take part). Idempotent: refreshes return the same
  // mode, so re-triggering is safe.
  useEffect(() => {
    if (isTestMode) return; // test mode resolves the condition synchronously
    if (!prolificPid || !studyId) return; // non-Prolific: mode set via the form
    if (!consentData) return; // wait for consent
    if (prolificMode) return; // already assigned
    let cancelled = false;
    setProlificLoading(true);

    (async () => {
      try {
        const { assigned_mode } = await getAssignedMode(studyId, prolificPid);
        if (!cancelled) {
          setProlificMode(assigned_mode);
          setProlificLoading(false);
        }
      } catch (err) {
        console.error("[Prolific] Mode assignment failed:", err);
        if (!cancelled) {
          setProlificError("Failed to assign study condition. Please reload or contact the researcher.");
          setProlificLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [isTestMode, prolificPid, studyId, consentData, prolificMode]);

  // ---- Logging ----
  const { logUserAction } = useLogging();

  const getParticipantCode = useCallback(() => participantCodeRef.current, []);
  const showAfterImageQuestionnaire = useCallback(
    (...args) => setShowAfterImageQuestionnaireRef.current(...args),
    []
  );

  // ---- Mode & image management ----
  const modeImageManager = useModeImageManager({
    assignedMode,
    logUserAction,
    setShowAfterImageQuestionnaire: showAfterImageQuestionnaire,
    getParticipantCode,
    restoredProgress: restored,
    onProgress: persist,
  });

  // ---- Questionnaire management ----
  const questionnaireManager = useQuestionnaireManager({
    logUserAction,
    phaseIndex: modeImageManager.phaseIndex,
    imageIndex: modeImageManager.imageIndex,
    assignedMode: modeImageManager.assignedMode,
    currentImage: modeImageManager.currentImage,
    imagesForMode: modeImageManager.imagesForMode,
    phaseOrder: PHASE_ORDER,
    setImageIndex: (...args) => setImageIndexRef.current(...args),
    setPhaseIndex: (...args) => setPhaseIndexRef.current(...args),
    participantCode: null,
    restoredProgress: restored,
    onProgress: persist,
  });

  // Sync refs + reactive state
  setImageIndexRef.current = modeImageManager.setImageIndex;
  setPhaseIndexRef.current = modeImageManager.setPhaseIndex;
  setShowAfterImageQuestionnaireRef.current =
    questionnaireManager.setShowAfterImageQuestionnaire;
  participantCodeRef.current =
    questionnaireManager.startingQuestionnaireData?.participantCode;

  // Keep assignedMode in sync with questionnaire data — state (not ref) so it triggers re-render
  const latestMode = questionnaireManager.startingQuestionnaireData?.assignedMode;
  const debugPrefill = isDebugPrefillParticipantCode(
    questionnaireManager.startingQuestionnaireData?.participantCode || testUser || prolificPid
  );
  useEffect(() => {
    if (latestMode && latestMode !== assignedMode) {
      console.log(`[App] assignedMode updated: "${latestMode}" (was "${assignedMode}")`);
      setAssignedMode(latestMode);
    }
  }, [latestMode, assignedMode]);

  // ---- Prolific completion handler ----
  const handleEndingSubmissionRef = useRef(questionnaireManager.handleEndingQuestionnaireSubmission);
  handleEndingSubmissionRef.current = questionnaireManager.handleEndingQuestionnaireSubmission;

  const handleEndingSubmission = useCallback(
    async (fd) => {
      await handleEndingSubmissionRef.current(fd);

      // Study finished — drop the resume snapshot so a later visit to the link
      // does not restore a completed session.
      clearSession(sessionKey);

      // If Prolific (not test mode), fetch redirect URL
      if (!isTestMode && prolificPid && studyId) {
        try {
          const { redirect_url } = await markComplete(prolificPid, studyId);
          setProlificRedirectUrl(redirect_url);
        } catch (err) {
          console.error("[Prolific] Completion failed:", err);
        }
      }
    },
    [isTestMode, prolificPid, studyId, sessionKey]
  );

  // ================================================================
  // Rendering
  // ================================================================

  // Prolific loading state
  if (prolificLoading) {
    return (
      <div className="root-container">
        <div className="app">
          <p>Loading study configuration...</p>
        </div>
      </div>
    );
  }

  if (prolificError) {
    return (
      <div className="root-container">
        <div className="app">
          <p style={{ color: "red" }}>{prolificError}</p>
        </div>
      </div>
    );
  }

  if (consentDeclined) {
    return (
      <div className="root-container">
        <div className="app">
          <div className="form-container">
            <h1>Study ended</h1>
            <p>
              You chose not to consent, so the study is now finished. No
              completion code has been issued and there is no payment for this
              session.
            </p>
            <p>
              Please go back to Prolific and <strong>return your submission</strong>{" "}
              (open the study on Prolific and choose “Stop without completing” →
              “Return submission”) so it does not count against you.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!accessData) {
    console.log(`[App] Rendering access screen: prolificMode="${prolificMode}", isTestMode=${isTestMode}, testUser=${testUser}, testCond=${testCond}`);
    return (
      <StartingQuestionnaire
        accessOnly={true}
        handleAccessComplete={setAccessData}
        prolificPid={isTestMode ? testUser : prolificPid}
        studyId={isTestMode ? "TEST" : studyId}
        assignedMode={prolificMode}
        showDebugControls={debugControlsEnabled}
      />
    );
  }

  if (!consentData) {
    return (
      <ConsentForm
        onConsent={setConsentData}
        onDecline={() => setConsentDeclined(true)}
      />
    );
  }

  // Prolific: consent is given but the condition assignment (triggered on
  // consent) hasn't returned yet — wait so the demographics form initializes
  // with the correct mode.
  if (!isTestMode && prolificPid && studyId && !prolificMode) {
    return (
      <div className="root-container">
        <div className="app">
          <p>Loading study configuration...</p>
        </div>
      </div>
    );
  }

  // 1. Starting questionnaire
  if (!questionnaireManager.startingQuestionnaireSubmitted) {
    console.log(`[App] Rendering StartingQuestionnaire: prolificMode="${prolificMode}", isTestMode=${isTestMode}, testUser=${testUser}, testCond=${testCond}`);
    return (
      <StartingQuestionnaire
        handleFormSubmission={
          questionnaireManager.handleStartingQuestionnaireSubmission
        }
        prolificPid={isTestMode ? testUser : prolificPid}
        studyId={isTestMode ? "TEST" : studyId}
        assignedMode={prolificMode}
        accessData={accessData}
        consentData={consentData}
        showDebugControls={debugControlsEnabled}
      />
    );
  }

  // 1b. Tutorial (after starting questionnaire, before main study)
  if (!tutorialCompleted) {
    return (
      <Tutorial
        assignedMode={assignedMode}
        onComplete={() => setTutorialCompleted(true)}
      />
    );
  }

  // 2. Ending questionnaire
  if (questionnaireManager.showEndingQuestionnaire) {
    const ea = questionnaireManager.getExplanationAvailability(
      modeImageManager.assignedMode
    );
    return (
      <EndingQuestionnaire
        handleFormSubmission={handleEndingSubmission}
        isVisualExplainationAvailable={ea.isVisualExplainationAvailable}
        isTextualExplanationAvailable={ea.isTextualExplanationAvailable}
        prolificRedirectUrl={prolificRedirectUrl}
        debugPrefill={debugPrefill}
      />
    );
  }

  // 3. After-phase questionnaire
  if (questionnaireManager.showAfterPhaseQuestionnaire) {
    const ea = questionnaireManager.getExplanationAvailability(
      modeImageManager.assignedMode
    );
    return (
      <AfterPhaseQuestionnaire
        handleFormSubmission={
          questionnaireManager.handleAfterPhaseQuestionnaireSubmission
        }
        isVisualExplainationAvailable={ea.isVisualExplainationAvailable}
        isTextualExplanationAvailable={ea.isTextualExplanationAvailable}
        debugPrefill={debugPrefill}
      />
    );
  }

  // 4. After-image questionnaire
  if (questionnaireManager.showAfterImageQuestionnaire) {
    const ea = questionnaireManager.getExplanationAvailability(
      modeImageManager.assignedMode
    );
    return (
      <AfterImageQuestionnaire
        handleFormSubmission={
          questionnaireManager.handleAfterImageQuestionnaireSubmission
        }
        isVisualExplainationAvailable={ea.isVisualExplainationAvailable}
        isTextualExplanationAvailable={ea.isTextualExplanationAvailable}
        debugPrefill={debugPrefill}
      />
    );
  }

  const phaseIntro = PHASE_INTROS[modeImageManager.currentPhase];
  if (
    tutorialCompleted &&
    phaseIntro &&
    !acknowledgedPhaseIntros[modeImageManager.currentPhase]
  ) {
    const introPhase = modeImageManager.currentPhase;
    return (
      <div className="root-container">
        <div className="app">
          <div className="form-container">
            <h1>{phaseIntro.title}</h1>
            <div className="section">
              <div className="phase-intro-people" aria-label="Trip participants">
                {phaseIntro.people.map((person) => (
                  <figure className="phase-intro-person" key={person.name}>
                    <img src={getOptimizedImageUrl(person.image)} alt={person.name} />
                    <figcaption>{person.name}</figcaption>
                  </figure>
                ))}
              </div>
              {phaseIntro.paragraphs.map((text) => (
                <p key={text}>{text}</p>
              ))}
            </div>
            <div className="navigation-buttons">
              <Button
                variant="contained"
                onClick={() =>
                  setAcknowledgedPhaseIntros((prev) => ({
                    ...prev,
                    [introPhase]: true,
                  }))
                }
              >
                Continue To The Next Trip
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 5. Image viewing stage
  console.log(`[App] Image viewing: assignedMode="${assignedMode}", modeImageManager.assignedMode="${modeImageManager.assignedMode}", currentImage="${modeImageManager.currentImage?.imageUrl}", imagesForMode=${modeImageManager.imagesForMode?.length}`);
  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        backgroundColor: "var(--bg-primary, #f8fafc)",
        position: "relative",
      }}
    >
      {/* Progress header */}
      <header className="progress-header">
        <span className="progress-header__phase">
          <strong>
            Phase {modeImageManager.phaseIndex + 1}/
            {modeImageManager.phaseOrder.length}: {PHASE_DISPLAY_NAMES[modeImageManager.currentPhase] || modeImageManager.currentPhase}
            {process.env.REACT_APP_NODE_ENV === "development" &&
              ` (${modeImageManager.assignedMode})`}
          </strong>
        </span>
        <span className="progress-header__title">
          <strong>Explore the image</strong>
        </span>
        <span className="progress-header__image">
          <strong>
            Image {modeImageManager.imageIndex + 1}/
            {modeImageManager.imagesForMode.length}
          </strong>
        </span>
      </header>

      <div className="app-container">
        {/* ----- Mode-specific layouts ----- */}
        {modeImageManager.assignedMode === "NO EXPLANATION" && (
          <div className="image-and-explanation-wrapper image-and-explanation-wrapper--solo">
            <div className="image-caption-wrapper">
              <ImageWithBoxes
                imageUrl={modeImageManager.currentImage?.imageUrl}
                imageWidth={modeImageManager.currentImage?.imageWidth}
                imageHeight={modeImageManager.currentImage?.imageHeight}
                segments={[]}
              />
              <CaptionText
                tokens={modeImageManager.getCurrentTokens()}
                entities={[]}
                requireSegmentForInteraction={true}
              />
            </div>
          </div>
        )}

        {modeImageManager.assignedMode === "SIMPLE VISUAL" && (
          <div className="image-and-explanation-wrapper">
            <div className="image-caption-wrapper">
              <ImageWithBoxes
                imageUrl={modeImageManager.currentImage?.imageUrl}
                imageWidth={modeImageManager.currentImage?.imageWidth}
                imageHeight={modeImageManager.currentImage?.imageHeight}
                hoveredId={modeImageManager.hoveredId}
                onImageHover={modeImageManager.onImageHover}
                onImageClick={modeImageManager.onImageClick}
                segments={modeImageManager.currentImage?.segments}
              />
              <CaptionText
                tokens={modeImageManager.getCurrentTokens()}
                entities={modeImageManager.currentImage?.segments}
                onCaptionHover={modeImageManager.onCaptionHover}
                onCaptionClick={modeImageManager.onCaptionClick}
                hoveredId={modeImageManager.hoveredId}
                hoveredTokenId={modeImageManager.hoveredTokenId}
                requireSegmentForInteraction={true}
              />
            </div>
          </div>
        )}

        {modeImageManager.assignedMode === "Interactive Textual XAI method" && (
          <div className="image-and-explanation-wrapper">
            <div className="image-caption-wrapper">
              <ImageWithBoxes
                imageUrl={modeImageManager.currentImage?.imageUrl}
                imageWidth={modeImageManager.currentImage?.imageWidth}
                imageHeight={modeImageManager.currentImage?.imageHeight}
                segments={[]}
                hideOverlay={true}
              />
              <CaptionText
                tokens={modeImageManager.getCurrentTokens()}
                entities={modeImageManager.currentImage?.segments}
                onCaptionHover={modeImageManager.onCaptionHover}
                onCaptionClick={modeImageManager.onCaptionClick}
                hoveredId={modeImageManager.hoveredId}
                hoveredTokenId={modeImageManager.hoveredTokenId}
              />
            </div>
            <div className="visual-textual-wrapper">
              <TextualExplanation
                explanations={modeImageManager.explanationObj?.textual}
                hoveredId={modeImageManager.hoveredId}
                showMetadata={false}
              />
            </div>
          </div>
        )}

        {modeImageManager.assignedMode === "Interactive Visual XAI method" && (
          <div className="image-and-explanation-wrapper">
            <div className="image-caption-wrapper">
              <ImageWithBoxes
                imageUrl={modeImageManager.currentImage?.imageUrl}
                imageWidth={modeImageManager.currentImage?.imageWidth}
                imageHeight={modeImageManager.currentImage?.imageHeight}
                hoveredId={modeImageManager.hoveredId}
                onImageHover={modeImageManager.onImageHover}
                onImageClick={modeImageManager.onImageClick}
                segments={modeImageManager.currentImage?.segments}
                hideOverlay={true}
              />
              <CaptionText
                tokens={modeImageManager.getCurrentTokens()}
                entities={modeImageManager.currentImage?.segments}
                onCaptionHover={modeImageManager.onCaptionHover}
                onCaptionClick={modeImageManager.onCaptionClick}
                hoveredId={modeImageManager.hoveredId}
                hoveredTokenId={modeImageManager.hoveredTokenId}
              />
            </div>
            <div className="visual-textual-wrapper">
              <VisualTextualExplanation
                hoveredId={modeImageManager.hoveredId}
                visualExplanations={modeImageManager.explanationObj?.visual}
                textualExplanations={null}
                showVisualReasoning={false}
                requireVisualEvidence={true}
                onEvidenceHover={modeImageManager.onEvidenceHover}
                onEvidenceClick={modeImageManager.onEvidenceClick}
              />
            </div>
          </div>
        )}

        {modeImageManager.assignedMode === "Interactive Complementary XAI method" && (
          <div className="image-and-explanation-wrapper">
            <div className="image-caption-wrapper">
              <ImageWithBoxes
                imageUrl={modeImageManager.currentImage?.imageUrl}
                imageWidth={modeImageManager.currentImage?.imageWidth}
                imageHeight={modeImageManager.currentImage?.imageHeight}
                hoveredId={modeImageManager.hoveredId}
                onImageHover={modeImageManager.onImageHover}
                onImageClick={modeImageManager.onImageClick}
                segments={modeImageManager.currentImage?.segments}
              />
              <CaptionText
                tokens={modeImageManager.getCurrentTokens()}
                entities={modeImageManager.currentImage?.segments}
                onCaptionHover={modeImageManager.onCaptionHover}
                onCaptionClick={modeImageManager.onCaptionClick}
                hoveredId={modeImageManager.hoveredId}
                hoveredTokenId={modeImageManager.hoveredTokenId}
              />
            </div>
            <div className="visual-textual-wrapper">
              <VisualTextualExplanation
                hoveredId={modeImageManager.hoveredId}
                visualExplanations={modeImageManager.explanationObj?.visual}
                textualExplanations={null}
                showVisualReasoning={true}
                onEvidenceHover={modeImageManager.onEvidenceHover}
                onEvidenceClick={modeImageManager.onEvidenceClick}
              />
            </div>
          </div>
        )}

        {/* Next / Finish button */}
        <div className="floating-next-btn">
          <Button
            variant="contained"
            onClick={modeImageManager.handleNextImage}
            size="large"
            sx={{
              background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
              },
            }}
          >
            {modeImageManager.imageIndex ===
            modeImageManager.imagesForMode.length - 1
              ? "Rate & finish trip →"
              : "Rate this caption →"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AppRouter;
